import React from 'react'
import * as styles from './search.control.styl'
import cx from 'classnames'
import type { History } from 'history'
import { Routes } from '@src/constants/routes'
import { invalidInput } from '@src/redux/reducers/autocomplete.query'
import { trim } from 'lodash'
import { FilterSuggestions } from './partials/filter.suggestions'
import { getTenant } from '@src/selectors'
import * as Selectors from './selectors/search.options'
import { CSSSelector } from '@src/utils/css.selector.helper'
import { isPageByPathName } from '@src/utils/router.helpers'
import { useStateCallback } from '@src/hooks'
import { keyboardKeys } from '@src/constants/key.codes'
import { useDispatch, useSelector } from 'react-redux'
import * as AutocompleteActions from '@src/redux/actions/autocomplete.actions'
import SearchBar from '@src/components/common/search.bar'

const urlSearchResultPage = Routes?.pages?.searchResult?.path

export const DELAY_SEARCH = 350

const placeholder = 'Search'

interface OwnProps {
  history: History
}

export type Props = OwnProps

interface State {
  focusIndex: number
  isVisibleSuggestions: boolean
}

type SearchControlComponent<P> = React.FC<P> & {
  CSSSelectors?: {
    headerSearchInput: CSSSelector
  }
}

const SearchControl: SearchControlComponent<Props> = React.memo(
  (props: Props) => {
    const inputRef = React.useRef<HTMLInputElement>()
    const dispatch = useDispatch()
    const tenant = useSelector(getTenant)
    const searchQuery = useSelector(Selectors.getSearchQuery)
    const searchData = useSelector(Selectors.getSearchDataSelector)
    const { history } = props

    const [state, setState] = useStateCallback<State>({
      focusIndex: -1,
      isVisibleSuggestions: false
    })

    function getInputQuery(input: string, withTrim = true) {
      if (!withTrim) {
        return `${input}\n`
      }
      return `${trim(input)}\n`
    }

    React.useEffect(() => {
      window.addEventListener('click', handleBlurInput)
      return () => {
        window.removeEventListener('click', handleBlurInput)
      }
    }, [])

    function handleChange(e: React.ChangeEvent<HTMLInputElement>) {
      // Prevents React from resetting its properties:
      e?.persist()
      setState(prev => ({
        ...prev,
        isVisibleSuggestions: !!e?.target?.value
      }))

      if (searchQuery !== `${getInputQuery(e?.target?.value, false)}`) {
        if ((e?.nativeEvent as InputEvent)?.inputType === 'insertFromPaste') {
          dispatch(
            AutocompleteActions.autocompleteGetOptions((e.target.value += ' '))
          )
        } else {
          dispatch(AutocompleteActions.autocompleteGetOptions(e.target.value))
        }
      }
    }

    function handleKeyPress(
      e: React.KeyboardEvent<HTMLInputElement> &
        React.InvalidEvent<HTMLInputElement>
    ) {
      if (e.key === keyboardKeys.enter) {
        if (state.focusIndex >= 0) {
          handleClick(searchData[state.focusIndex])
          return
        }
        dispatch(
          AutocompleteActions.prepSession(
            `${getInputQuery(e.target.value)}`,
            () => {
              if (inputRef.current) {
                inputRef.current.blur()
              }
              if (!isPageByPathName(urlSearchResultPage, history)) {
                return history.push(urlSearchResultPage)
              }
              dispatch(AutocompleteActions.autocompleteSessionSearch())
            }
          )
        )
      }
    }

    function handleKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
      if (e.key === keyboardKeys.arrowDown) {
        let focusIndex = state.focusIndex + 1
        if (searchData.length - 1 < focusIndex) {
          focusIndex = 0
        }
        setState(prev => ({ ...prev, focusIndex }))
      }

      if (e.key === keyboardKeys.arrowUp) {
        let focusIndex = state.focusIndex - 1
        if (focusIndex < 0) {
          focusIndex = searchData.length - 1
        }
        setState(prev => ({ ...prev, focusIndex }))
        e.preventDefault()
      }

      if (
        e.key === keyboardKeys.tab &&
        searchData.length >= 1 &&
        searchData[0] !== invalidInput
      ) {
        const { focusIndex } = state
        const index = focusIndex >= 0 ? focusIndex : 0
        handleClick(searchData[index])
        e.preventDefault()
      }
    }

    function handleMouseOver() {
      setState(prev => ({ ...prev, focusIndex: -1 }))
    }

    function handleClick(
      text,
      event?: React.MouseEvent<HTMLDivElement, MouseEvent>
    ) {
      if (event) {
        event.preventDefault()
        event.stopPropagation()
      }
      const input = searchQuery
      const searchArray = input.split(' ')
      const lastValue = searchArray[searchArray.length - 1]
      let searchValue = input.indexOf(' ') >= 0 ? input + text : text
      if ((text || '').indexOf(lastValue) >= 0) {
        searchArray[searchArray.length - 1] = text
        searchValue = searchArray.join(' ')
      }
      dispatch(AutocompleteActions.autocompleteGetOptions(searchValue + ' '))

      setState(
        prev => ({
          ...prev,
          focusIndex: -1,
          isVisibleSuggestions: false
        }),
        () => {
          if (inputRef.current) {
            inputRef.current.blur()
            inputRef.current.focus()
          }
        }
      )
    }

    function handleFocus() {
      const searchValue = trim(searchQuery)
      if (!searchValue) {
        dispatch(AutocompleteActions.autocompleteGetOptions(''))
      } else {
        dispatch(AutocompleteActions.autocompleteGetOptions(searchValue + ' '))
      }
      setState(prev => ({ ...prev, isVisibleSuggestions: true }))
    }

    function handleBlurInput(event: Event) {
      if (
        inputRef.current &&
        !inputRef.current.contains(event.target as Node)
      ) {
        inputRef.current.blur()
        setState(prev => ({
          ...prev,
          isVisibleSuggestions: false,
          focusIndex: -1
        }))
      }
    }

    function clearSearch() {
      setState(prev => ({
        ...prev,
        isVisibleSuggestions: false
      }))
      dispatch(AutocompleteActions.saveSearchQuery(''))
    }

    const searchDisabled =
      !tenant || !tenant.account_info || tenant.account_info.length === 0

    return (
      <div data-role={`topSearch`} className={styles.searchControlWrapper}>
        <SearchBar
          id={SearchControl.CSSSelectors.headerSearchInput.value}
          autoComplete="off"
          className={styles.wrapper}
          inputClassName={styles.search}
          placeholder={placeholder}
          searchValue={searchQuery}
          onChange={handleChange}
          onFocus={handleFocus}
          onClear={clearSearch}
          isClearable={(searchQuery || '')?.length > 0}
          onKeyPress={handleKeyPress}
          onKeyDown={handleKeyDown}
          disabled={searchDisabled}
          ref={inputRef}
          debounced
          debounceWait={DELAY_SEARCH}
        />
        <div
          data-role={`dropdownSuggestions`}
          className={cx(styles.dropdownWrapper, {
            [styles.visible]: state.isVisibleSuggestions
          })}
        >
          <FilterSuggestions
            searchData={searchData}
            focusIndex={state.focusIndex}
            onClickSuggestion={handleClick}
            onMouseOver={handleMouseOver}
          />
        </div>
      </div>
    )
  }
)

SearchControl.CSSSelectors = {
  headerSearchInput: new CSSSelector('headerSearchInput')
}

export default SearchControl
